import { Card as AntCard } from 'antd';
import React from 'react';
import styled from 'styled-components';
import { theme } from '../../theme/semanticTokens';
import { Typography } from '../../Typography';

export type AmountCardHelperTexts = [string] | [string, string];

export interface AmountCardProps {
  label: string;
  value: string;
  helperTexts?: AmountCardHelperTexts;
  valueAdornment?: React.ReactNode;
  className?: string;
  'data-testid'?: string;
}

const StyledCard = styled(AntCard)`
  &.ant-card {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: fit-content;
    min-width: 320px;
    min-height: 140px;
    border-radius: ${theme.dime.radius.m};
    background-color: ${theme.dime.colors.surface.secondary};
    border: 1px solid ${theme.dime.colors.border.static};
    box-shadow: none;

    .ant-card-body {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: ${theme.dime.spacing.xl};
      gap: ${theme.dime.spacing.m};
      width: 100%;

      &::before,
      &::after {
        display: none;
      }
    }
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.dime.spacing.s};
`;

const ValueRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.dime.spacing.s};
`;

const HelperRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.dime.spacing.l};
  width: 100%;
`;

const HelperItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.dime.spacing.s};
`;

export const AmountCard: React.FC<AmountCardProps> = ({
  label,
  value,
  helperTexts,
  valueAdornment,
  className,
  'data-testid': dataTestId
}) => (
  <StyledCard
    bordered={false}
    className={className}
    data-testid={dataTestId ?? 'amount-card'}
  >
    <HeaderRow>
      <Typography variant="body2Bold" color={theme.dime.colors.text.secondary}>
        {label}
      </Typography>
    </HeaderRow>
    <ValueRow>
      <Typography variant="heading3Bold" color={theme.dime.colors.text.primary}>
        {value}
      </Typography>
      {valueAdornment}
    </ValueRow>
    {helperTexts && (
      <HelperRow>
        {helperTexts.map((text, index) => (
          <HelperItem key={`helperText-${index}`}>
            <Typography variant="body1" color={theme.dime.colors.text.secondary}>
              {text}
            </Typography>
          </HelperItem>
        ))}
      </HelperRow>
    )}
  </StyledCard>
);
